import React from 'react';
import { useIntl } from 'react-intl';
import { BffBonusData } from '@lucky7ventures/bff-types';

import { ModalLayout } from '@/features/modal/modals/ModalLayout';
import { OpenedFrom } from '@/lib/gTagManager';
import useCreateCmsCtaData from '@/hooks/useCreateCmsCtaData';
import { BaseModalProps } from '@/features/modal/types';
import { cdnBonusImg } from '@/utils/image-utils';

interface BonusCardProps {
  imageLink: string | null;
  name: string | null;
  closeModal: VoidFunction;
  action: VoidFunction | undefined;
}

export const BonusCard = ({ imageLink, name, closeModal, action }: BonusCardProps) => {
  const handleClick = () => {
    if (action) {
      action();
    }
    closeModal();
  };

  const imageSrc = imageLink
    ? cdnBonusImg(imageLink, {
        width: '400',
        height: '200',
        dpr: '2',
        gravity: 'center',
      })
    : undefined;

  return (
    <button
      type="button"
      onClick={handleClick}
      className="w-full cursor-pointer text-left focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:rounded-xl"
    >
      <div className="rounded-xl overflow-hidden">
        {imageSrc && (
          <img
            src={imageSrc}
            alt={name ?? 'bonus_image'}
            className="block h-[140px] w-full bg-white object-cover object-center"
          />
        )}
        <div className="relative z-10 bg-[#00262B] px-3 py-1.5 leading-[1.1]">
          <span className="block text-white text-xs font-bold">{name}</span>
        </div>
      </div>
    </button>
  );
};

export interface BonusModalBaseProps {
  bonuses: BffBonusData[];
  titleId: string;
  subtitleId?: string;
  showContinueWithout?: boolean;
}

export const BonusModalBase = ({
  closeModal,
  bonuses,
  titleId,
  subtitleId,
  showContinueWithout,
}: BonusModalBaseProps & Pick<BaseModalProps, 'closeModal'>) => {
  const intl = useIntl();
  const { createAction } = useCreateCmsCtaData();
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [showFade, setShowFade] = React.useState(false);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setShowFade(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  };

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowFade(el.scrollHeight > el.clientHeight);
  }, [bonuses]);

  return (
    <ModalLayout
      close={closeModal}
      className={`!overflow-y-hidden !max-h-[calc(100dvh-3rem)] ${
        bonuses.length === 1 ? '!max-w-sm' : '!max-w-3xl'
      }`}
    >
      <div className="flex flex-col w-full px-4 max-h-[calc(100dvh-10rem)]">
        <div className="text-center mb-4 flex-shrink-0">
          <span className="text-[15px] sm:text-[17px] font-bold">
            {intl.formatMessage({ id: titleId })}
          </span>
          {subtitleId && (
            <span className="text-center block w-full text-[15px]">
              {intl.formatMessage({ id: subtitleId })}
            </span>
          )}
        </div>
        <div className="relative flex-1 min-h-0">
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className={`overflow-y-auto h-full no-scrollbar grid grid-cols-1 gap-4 ${
              bonuses.length > 1 ? 'sm:grid-cols-2' : ''
            }`}
          >
            {bonuses.map(bonus => {
              const action =
                createAction(
                  OpenedFrom.BonusesAvailable,
                  bonus.authenticatedPrimaryCtaType,
                  bonus.authenticatedPrimaryCtaLink,
                  bonus.authenticatedPrimaryCtaAnchorId,
                  bonus.promoCode,
                  bonus.customTermsAndConditionsDescription,
                ) ??
                createAction(
                  OpenedFrom.BonusesAvailable,
                  bonus.primaryCtaType,
                  bonus.primaryCtaLink,
                  bonus.primaryCtaAnchorId,
                  bonus.promoCode,
                  bonus.customTermsAndConditionsDescription,
                );
              return (
                <BonusCard
                  key={bonus.slug}
                  action={action}
                  imageLink={bonus.image}
                  name={bonus.heading}
                  closeModal={closeModal}
                />
              );
            })}
          </div>
          {showFade && (
            <div className="absolute bottom-0 left-0 right-0 h-5 bg-gradient-to-t from-white to-transparent pointer-events-none" />
          )}
        </div>
        {showContinueWithout && (
          <button
            type="button"
            className="text-center mt-4 block w-full text-[15px] flex-shrink-0"
            onClick={() => closeModal()}
          >
            {intl.formatMessage({ id: 'welcomeOffer.continueWithout' })}
          </button>
        )}
      </div>
    </ModalLayout>
  );
};
